import { useState } from 'react'
import { PageHeader, StatTile, Panel, SegmentedControl, Banner, Chip, BarChart } from '../ds/index'
import { HBars } from '../components/Viz'
import { useApi } from '../lib/useApi'
import { num, dateShort } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type Check = {
  days: number; total_emails: number
  kinds: { group_post: number; digest: number; other_facebook: number; not_facebook: number; unknown_sender: number }
  checks: {
    one_email_per_post: { digests: number; digest_share: number | null }
    full_text: { truncated: number; no_text: number; full_share: number | null }
    post_link: { with_link: number; share: number | null }
    group_name: { with_group: number; share: number | null }
  }
  dedupe: { posts: number; emails: number; seen_more_than_once: number }
  per_group: { id: string; name: string; auto_created: boolean; emails: number; posts: number; leads: number; active_days: number; posts_per_day: string }[]
  per_day: { day: string; emails: number; post_emails: number; digests: number; posts: number }[]
}

/** Pass at 95% or better, worry below 80. A guess at the line, stated so it can be argued with. */
function verdict(share: number | null, invert = false) {
  if (share == null) return { tone: 'warn' as const, text: 'No emails yet' }
  const good = invert ? share <= 5 : share >= 95
  const bad = invert ? share >= 20 : share < 80
  return good ? { tone: 'pos' as const, text: 'Works' } : bad ? { tone: 'neg' as const, text: 'Does not work as designed' } : { tone: 'warn' as const, text: 'Partly' }
}

export default function FormatCheck() {
  const [days, setDays] = useState('7')
  const { data, error } = useApi<Check>(`/api/intake/format-check?days=${days}`)
  if (error) return <ErrorBlock error={error} />


  const c = data?.checks
  const tiles = c && [
    { label: 'One email per post', value: `${num(data!.kinds.digest)} digest${data!.kinds.digest === 1 ? '' : 's'}`, meta: `${c.one_email_per_post.digest_share ?? 0}% of post emails were digests`, v: verdict(c.one_email_per_post.digest_share, true) },
    { label: 'Full post text', value: c.full_text.full_share == null ? 'n/a' : `${c.full_text.full_share}%`, meta: `${c.full_text.truncated} cut off, ${c.full_text.no_text} with no text`, v: verdict(c.full_text.full_share) },
    { label: 'Post link present', value: c.post_link.share == null ? 'n/a' : `${c.post_link.share}%`, meta: `${num(c.post_link.with_link)} of ${num(data!.kinds.group_post)} post emails`, v: verdict(c.post_link.share) },
    { label: 'Group name present', value: c.group_name.share == null ? 'n/a' : `${c.group_name.share}%`, meta: `${num(c.group_name.with_group)} of ${num(data!.kinds.group_post)} post emails`, v: verdict(c.group_name.share) },
  ]

  return (
    <>
      <PageHeader title="Format check" subtitle="The test the whole design depends on: does Facebook send one usable email per post? Read it off real emails, not the demo ones."
                  actions={<SegmentedControl value={days} onChange={setDays} segments={[{ value: '1', label: '1 day' }, { value: '7', label: '7 days' }, { value: '30', label: '30 days' }]} />} />
      {!data ? <LoadingBlock /> : (
        <>
          {data.total_emails === 0 && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title="No emails in this window">Connect the mailbox on the Mailbox page, or paste the test account's notifications on Emails in.</Banner></div>}
          <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
            {tiles!.map((t) => (
              <StatTile key={t.label} label={t.label} value={t.value}
                        meta={<><div style={{ marginBottom: 'var(--s-3)' }}><Chip tone={t.v.tone} dot>{t.v.text}</Chip></div>{t.meta}</>} />
            ))}
          </div>
          <div className="grid-tiles" style={{ marginBottom: 'var(--s-7)' }}>
            <StatTile focal label="Unique posts" value={num(data.dedupe.posts)} meta={`from ${num(data.dedupe.emails)} post emails; ${num(data.dedupe.seen_more_than_once)} arrived more than once`} />
            <StatTile label="All emails" value={num(data.total_emails)} meta={[`${num(data.kinds.other_facebook)} other Facebook`, data.kinds.unknown_sender ? `${num(data.kinds.unknown_sender)} Facebook post${data.kinds.unknown_sender === 1 ? '' : 's'} from an address not tied to a rep (held)` : null, `${num(data.kinds.not_facebook)} not Facebook`].filter(Boolean).join(', ')} />
            <StatTile label="Groups sending" value={num(data.per_group.length)} meta={`${data.days} day window`} />
          </div>
          <div className="two-col">
            <Panel>
              <BarChart title="Emails per day" subtitle="Post emails, the unique posts inside them, and digests. In the org's time zone." data={data.per_day.map((d) => ({ ...d, day: dateShort(d.day) }))} xKey="day"
                        series={[{ key: 'post_emails', label: 'Post emails' }, { key: 'posts', label: 'Unique posts' }, { key: 'digests', label: 'Digests' }]} height={260} showTable />
            </Panel>
            <Panel>
              <HBars title="Posts per day, by group" subtitle="This is what AI cost and price scale with" format={(n) => n.toLocaleString('en-US', { maximumFractionDigits: 1 })}
                     empty="No group posts in this window."
                     rows={data.per_group.map((g) => ({ label: g.name, value: Number(g.posts_per_day), note: `${num(g.posts)} posts, ${num(g.emails)} emails, ${num(g.leads)} leads${g.auto_created ? ', not set up' : ''}` }))} />
            </Panel>
          </div>
        </>
      )}
    </>
  )
}
